// src/hooks/useAccessRequests.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth }        from '../contexts/AuthContext';
import { initializeApp }  from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { auth, firebaseConfig } from '../firebase/firebase';
import { useAuthState }         from 'react-firebase-hooks/auth';

import { generateCompanyId, fetchCompaniesForIdGen } from '../utils/generalUtils';

import {
  fetchAccessRequests,
  updateRequestStatus,
  checkEmailExists,
  createUserDocument,
  createCompanyDocument,
  fetchCompanyByCompanyId,
  updateCompanyDocument,
  logAuditEntry,
} from '../services/accessRequestsService.js';

import {
  sendAccessApprovalEmail,
  sendAccessRejectionEmail,
} from '../services/brevoEmailService';

const generateRandomPassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  return Array.from({ length: 12 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
};

export const useAccessRequests = () => {
  const [firebaseUser] = useAuthState(auth);
  const { currentUser } = useAuth();

  // ── Secondary Firebase app (admin never logs out) ─────────────────────
  const secondaryAppRef  = useRef(null);
  const secondaryAuthRef = useRef(null);
  if (!secondaryAppRef.current) {
    secondaryAppRef.current  = initializeApp(firebaseConfig, 'Secondary');
    secondaryAuthRef.current = getAuth(secondaryAppRef.current);
  }

  // ── State ──────────────────────────────────────────────────────────────
  const [userRequests,  setUserRequests]  = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [fetchError,    setFetchError]    = useState('');
  const [notification,  setNotification]  = useState('');
  const [processing,    setProcessing]    = useState(false);
  const [lastAction,    setLastAction]    = useState(null);

  // ── Normalise ──────────────────────────────────────────────────────────
  const normaliseRequest = useCallback((doc, requestType) => ({
    company:        doc.company        || '',
    companyId:      doc.companyId      || '',
    departmentName: doc.departmentName || 'Not specified',
    ...doc,
    requestType,
    role: doc.requestedRole || doc.role || 'user',
  }), []);

  // ── Load requests via service → repo ──────────────────────────────────
  const loadRequests = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setFetchError('');

    try {
      const { userRequests: userDocs, adminRequests: adminDocs } =
        await fetchAccessRequests(
          currentUser.role,
          currentUser.companyId,
          currentUser.company,
        );

      setUserRequests(userDocs.map(d  => normaliseRequest(d, 'user')));
      setAdminRequests(adminDocs.map(d => normaliseRequest(d, 'admin')));
    } catch (err) {
      console.error('[useAccessRequests] loadRequests error:', err);
      setFetchError(err.message);
      setUserRequests([]);
      setAdminRequests([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, normaliseRequest]);

  useEffect(() => {
    if (currentUser && ['admin', 'superadmin'].includes(currentUser.role)) {
      loadRequests();
    } else {
      setLoading(false);
    }
  }, [currentUser, loadRequests]);

  // ── Approve ────────────────────────────────────────────────────────────
  const approveRequest = async (selectedRow, selectedRole, password) => {
    if (!selectedRow || !password) {
      return { error: 'Password is required' };
    }

    setProcessing(true);
    setFetchError('');
    let authUserCreated = null;
    let newUid          = null;

    try {
      // 1. Email check via service → repo
      const emailExists = await checkEmailExists(selectedRow.email);
      if (emailExists) {
        setProcessing(false);
        return { emailConflict: true };
      }

      // 2. Create Firebase Auth user
      const cred = await createUserWithEmailAndPassword(
        secondaryAuthRef.current,
        selectedRow.email,
        password,
      );
      authUserCreated = cred.user;
      newUid          = cred.user.uid;
      await signOut(secondaryAuthRef.current);

      // 3. Determine company info
      let companyId, companyName;
      if (selectedRow.requestType === 'admin' && selectedRole === 'admin') {
        // ── CHANGED: fetch companies once, then call the pure generator ──
        const companiesNow = await fetchCompaniesForIdGen();
        companyId   = generateCompanyId(selectedRow.company, companiesNow);
        companyName = selectedRow.company;
      } else {
        companyId   = selectedRow.companyId?.trim().toUpperCase();
        companyName = selectedRow.company;
        if (!companyId || !companyName) {
          throw new Error(`Request missing company info. companyId: ${companyId}`);
        }
      }

      // 4. Create user document via service → repo
      const userDoc = {
        uid:                 newUid,
        name:                selectedRow.name,
        email:               selectedRow.email,
        role:                selectedRole,
        company:             companyName,
        companyId:           companyId,
        location:            selectedRow.location,
        description:         selectedRow.description || '',
        createdAt:           new Date().toISOString(),
        emailVerified:       false,
        isActive:            true,
        createdByAdmin:      firebaseUser?.uid,
        approvedFromRequest: selectedRow.id,
        ...(selectedRow.departmentId   && { departmentId:   selectedRow.departmentId   }),
        ...(selectedRow.departmentName && { departmentName: selectedRow.departmentName }),
        ...(selectedRow.city           && { city:           selectedRow.city           }),
        ...(selectedRow.stateCode      && { stateCode:      selectedRow.stateCode      }),
      };

      await createUserDocument(newUid, userDoc);

      // 5. Mark request approved via service → repo
      await updateRequestStatus(
        selectedRow.requestType,
        selectedRow.id,
        'approved',
        { approvedRole: selectedRole, createdUid: newUid, companyId },
        firebaseUser?.uid,
      );

      // 6. Handle company via service → repo
      if (selectedRole === 'admin' && selectedRow.requestType === 'admin') {
        await createCompanyDocument(companyId, {
          name:          companyName,
          companyId,
          email:         selectedRow.email,
          contactPerson: selectedRow.name,
          address:       '—',
          city:          selectedRow.city      || 'Udupi',
          state:         selectedRow.stateCode || 'Karnataka',
          pincode:       '',
          phoneNumber:   '',
          gstin:         '',
          website:       '',
          status:        'Active',
          assets:        1,
          userCount:     1,
          adminCount:    1,
          admins:        [newUid, firebaseUser?.uid],
          createdAt:     new Date().toISOString(),
          createdBy:     firebaseUser?.uid,
          updatedAt:     new Date().toISOString(),
          updatedBy:     firebaseUser?.email,
        });
      } else {
        const existingCompany = await fetchCompanyByCompanyId(companyId);
        if (existingCompany) {
          const updates = selectedRole === 'admin'
            ? {
                admins:     [...(existingCompany.admins || []), newUid],
                adminCount: (existingCompany.adminCount || 0) + 1,
                userCount:  (existingCompany.userCount  || 0) + 1,
                updatedAt:  new Date().toISOString(),
                updatedBy:  firebaseUser?.email,
              }
            : {
                userCount: (existingCompany.userCount || 0) + 1,
                updatedAt: new Date().toISOString(),
                updatedBy: firebaseUser?.email,
              };
          await updateCompanyDocument(existingCompany.id, updates);
        }
      }

      // 7. Audit log via service → repo
      await logAuditEntry(
        'ACCESS_REQUEST_APPROVED',
        firebaseUser?.uid,
        firebaseUser?.email,
        selectedRow,
        { approvedRole: selectedRole, companyIdUsed: companyId, createdUid: newUid },
      );

      // 8. Send approval email
      try {
        await sendAccessApprovalEmail({
          name: selectedRow.name, email: selectedRow.email,
          role: selectedRole, password, companyName,
        });
      } catch (emailErr) {
        console.warn('[useAccessRequests] Approval email failed:', emailErr);
      }

      // 9. Update local state
      if (selectedRow.requestType === 'admin') {
        setAdminRequests(p => p.filter(r => r.id !== selectedRow.id));
      } else {
        setUserRequests(p => p.filter(r => r.id !== selectedRow.id));
      }

      setNotification(
        `"${selectedRow.name}" approved for company "${companyName}" (${companyId})`
      );
      setLastAction({ type: 'approved', data: selectedRow, time: Date.now() });
      return { success: true };

    } catch (err) {
      console.error('[useAccessRequests] approveRequest error:', err);
      if (authUserCreated) {
        try { await authUserCreated.delete(); } catch (_) {}
      }
      const errMsg =
        err.code === 'auth/email-already-in-use' ? 'Email already registered in Firebase Auth.' :
        err.code === 'auth/weak-password'         ? 'Password must be at least 6 characters.'   :
        err.code === 'permission-denied'          ? `Permission denied: ${err.message}`          :
        `Error: ${err.message}`;
      setFetchError(errMsg);
      return { error: errMsg };
    } finally {
      setProcessing(false);
    }
  };

  // ── Reject ─────────────────────────────────────────────────────────────
  const rejectRequest = async (req, reason = '') => {
    setProcessing(true);
    try {
      await updateRequestStatus(
        req.requestType, req.id, 'rejected',
        { reason }, firebaseUser?.uid,
      );
      await logAuditEntry(
        'ACCESS_REQUEST_REJECTED',
        firebaseUser?.uid,
        firebaseUser?.email,
        req,
        { reason },
      );
      try {
        await sendAccessRejectionEmail({
          name: req.name, email: req.email,
          role: req.role || 'user', reason,
          requestType: req.requestType || 'user',
        });
      } catch (emailErr) {
        console.warn('[useAccessRequests] Rejection email failed:', emailErr);
      }
      if (req.requestType === 'admin') {
        setAdminRequests(p => p.filter(r => r.id !== req.id));
      } else {
        setUserRequests(p => p.filter(r => r.id !== req.id));
      }
      setNotification(`"${req.name}" rejected and notified via email.`);
      setLastAction({ type: 'rejected', data: req, time: Date.now() });
      return { success: true };
    } catch (err) {
      console.error('[useAccessRequests] rejectRequest error:', err);
      setFetchError(err.message);
      return { error: err.message };
    } finally {
      setProcessing(false);
    }
  };

  // ── Undo ───────────────────────────────────────────────────────────────
  const undoLastAction = async () => {
    if (!lastAction) return;
    setProcessing(true);
    try {
      await updateRequestStatus(
        lastAction.data.requestType,
        lastAction.data.id,
        'pending',
        {},
        firebaseUser?.uid,
      );
      const restored = { ...lastAction.data, status: 'pending' };
      if (lastAction.data.requestType === 'admin') {
        setAdminRequests(p => [...p, restored]);
      } else {
        setUserRequests(p => [...p, restored]);
      }
      setNotification('Action undone.');
      setLastAction(null);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return {
    userRequests,
    adminRequests,
    loading,
    fetchError,
    notification,
    processing,
    lastAction,
    approveRequest,
    rejectRequest,
    undoLastAction,
    generateRandomPassword,
    setFetchError,
    setNotification,
    setLastAction,
  };
};